import { Dialog, Switch, Transition } from '@headlessui/react'
import { XIcon } from '@heroicons/react/solid'
import Translation from 'components/Translation'
import { useAppContext } from 'context/AppContext'
import { AuthContext, useAuth } from 'context/AuthContext'
import { useTenantContext } from 'context/TenantContext'
import { Fragment, SetStateAction, useEffect, useState } from 'react'
import {
    createChargebeeCustomer,
    createSubscription,
    getCoupon,
    parseChargebeeAmount,
    updateSubscription,
} from 'services/chargebee'
import {
    ChargebeeSubscriptionDto,
    ChargebeeSubscriptionItemDto,
    CouponDto,
    SubscriptionDto,
    SubscriptionItemDto,
} from 'types/subscription'
import { classNames } from 'utils/dom-helpers'
import { getTranslation } from 'utils/language-helper'
import { Card } from '../CreditCard/types'
import { createModal } from '../ModalFactory'
import { ModalWrapper } from '../ModalWrapper'
import getConfig from 'next/config'

const ModalProvider = createModal(
    AuthContext,
    'PaymentModal',
    () => (
        <>
            <i className="feather feather-plus mr-4" />
            <span className="circular">Payment</span>
        </>
    ),
    () => (
        <span className="inline-block w-6 h-6 text-primary text-center font-extralight">
            <i className="feather feather-x " />
        </span>
    )
)

export const PaymentModalOpener = ModalProvider.Opener
export const PaymentModalCloser = ModalProvider.Closer

export enum PurchaseType {
    Upgrade,
    Downgrade,
    AddOn,
}

export type PaymentModalProps = {
    purchase_type: PurchaseType
    subscription: SubscriptionDto
    updated_subscription_dto: ChargebeeSubscriptionDto
    new_subscription_items: ChargebeeSubscriptionItemDto[]
    primary_card: Card
    selected_plan: string
}

export default function PaymentModal() {
    const { translations, setGlobalLoading } = useAppContext()
    const {
        CreditCardModal: _CreditCardModal,
        PaymentModal,
        user_profile,
    } = useAuth()
    const { tenant } = useTenantContext()
    const { attributes, setAttributes } = PaymentModal
    const [api_error, setApiError] = useState('')
    const { NEXT_PUBLIC_STATIC_DOMAIN } = getConfig().publicRuntimeConfig
    const [current_subscription, setCurrentSubscription] =
        useState<SubscriptionDto>()
    const [updated_subscription, setUpdatedSubscription] =
        useState<ChargebeeSubscriptionDto>()
    const [new_subscription_items, setNewSubscriptionItems] = useState<
        ChargebeeSubscriptionItemDto[]
    >([])
    const [current_plan, setCurrentPlan] = useState<SubscriptionItemDto>()
    const [primary_card, setPrimaryCard] = useState<Card>()

    const [purchase_type, setPurchaseType] = useState<PurchaseType>()
    const [discount, setDiscount] = useState(0)
    const [subtotal, setSubtotal] = useState(0)
    const [total, setTotal] = useState(0)

    const [apply_coupon, toggleApplyCoupon] = useState<boolean>(false)
    const [coupon, setCoupon] = useState<CouponDto | undefined>()
    const [coupon_message, setCouponMessage] = useState('')

    const [refetch_subscription, setFetch] = useState<boolean>(false)
    const [purchasing, setPurchasing] = useState<boolean>(false)
    const [display_thank_you_modal, setDisplayThankYouModal] =
        useState(false)
    useEffect(() => {
        if (!PaymentModal.is_open) {
            setAttributes({
                refetch_subscription: refetch_subscription,
            })
            setFetch(false)
            toggleApplyCoupon(false)
            setCoupon(undefined)
        }
    }, [PaymentModal.is_open])

    useEffect(() => {
        if (attributes) {
            setCurrentSubscription(
                attributes.subscription as SubscriptionDto
            )
            setUpdatedSubscription(
                attributes.updated_subscription_dto as ChargebeeSubscriptionDto
            )
            setPurchaseType(attributes.purchase_type as PurchaseType)
            setNewSubscriptionItems(
                attributes.new_subscription_items as ChargebeeSubscriptionItemDto[]
            )
            if (attributes.primary_card) {
                setPrimaryCard(attributes.primary_card as Card)
            }
        }
    }, [attributes])

    useEffect(() => {
        if (current_subscription?.subscription_items) {
            let current_plan = current_subscription.subscription_items.find(
                (item) => item.item_type == 'plan'
            )
            setCurrentPlan(current_plan)
        }
    }, [current_subscription])

    useEffect(() => {
        if (new_subscription_items) {
            let total = 0
            let subtotal = 0
            switch (purchase_type) {
                case PurchaseType.Upgrade:
                    if (current_plan) {
                        subtotal =
                            new_subscription_items[0]
                                .subscriptionItemUnitPrice -
                            current_plan.amount
                    } else {
                        subtotal =
                            new_subscription_items[0]
                                .subscriptionItemUnitPrice
                    }
                    total =
                        new_subscription_items[0].subscriptionItemUnitPrice
                    break
                case PurchaseType.Downgrade:
                    if (current_plan) {
                        if (new_subscription_items[0]) {
                            subtotal =
                                new_subscription_items[0]
                                    .subscriptionItemUnitPrice
                        }
                    }
                    total = subtotal
                    break
                case PurchaseType.AddOn:
                    new_subscription_items.forEach(
                        (item: ChargebeeSubscriptionItemDto) => {
                            subtotal +=
                                item.subscriptionItemUnitPrice *
                                item.subscriptionItemQuantity
                        }
                    )
                    total = subtotal
                    break
            }

            let coupon_message = ''
            let discount = 0
            if (apply_coupon && coupon) {
                let coupon_detail = ''
                if (coupon.discount_type == 'percentage') {
                    discount = (total * coupon.discount_percentage) / 100
                    coupon_detail = `${coupon.discount_percentage}%`
                } else {
                    discount = total - coupon.discount_amount
                    coupon_detail = `USD ${parseChargebeeAmount(
                        coupon.discount_amount
                    )}`
                }

                total = total - discount
                coupon_message = `${getTranslation(
                    'coupon_message',
                    translations
                )} ${coupon_detail} ${coupon.invoice_name}.`
            }

            setCouponMessage(coupon_message)
            setDiscount(discount)
            setSubtotal(subtotal)
            setTotal(total)
        }
    }, [coupon, apply_coupon, new_subscription_items, current_plan])

    async function handleCouponChange(e: any) {
        const coupon_id: string = e.target.value.trim() // cbdemo_specialoffer, cbdemo_usd100
        setCoupon(undefined)
        setCouponMessage('')
        if (coupon_id) {
            let coupon = await getCoupon(coupon_id)
            if (coupon?.status == 'active') {
                setCoupon(coupon)
            } else {
                setCouponMessage(
                    getTranslation('invalid_coupon', translations)
                )
            }
        }
    }

    function handleAddCard() {
        _CreditCardModal.setAttributes({
            back_button_label: getTranslation(
                'back_to_payment',
                translations
            ),
        })
        _CreditCardModal.open()
    }

    async function handlePayment() {
        if (!tenant || !updated_subscription) return

        try {
            setGlobalLoading(true)
            setPurchasing(true)

            let api = null
            if (!current_subscription?.id) {
                let customer_id = tenant?.chargebeeCustomerId as string
                if (!customer_id) {
                    const customer = await createChargebeeCustomer(
                        tenant,
                        user_profile
                    )
                    customer_id = customer.id
                }
                api = await createSubscription(
                    customer_id,
                    updated_subscription,
                    coupon
                )
            } else {
                api = await updateSubscription(
                    current_subscription.id,
                    updated_subscription,
                    coupon
                )
            }

            if (api && api.ok) {
                setFetch(true)
                setDisplayThankYouModal(true)
                toggleApplyCoupon(false)
                setCoupon(undefined)
                setCouponMessage('')
                // PaymentModal.close()
            } else {
                throw api.message
            }
        } catch (err) {
            setApiError(`Payment Failed! ${err}`)
        } finally {
            setGlobalLoading(false)
            setPurchasing(false)
        }
    }

    function formatAmount(value: number | undefined) {
        const amount = parseChargebeeAmount(value || 0).toFixed(2)
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        return `USD ${amount}`
    }

    function RenderPurchaseDetails() {
        const duration = <Translation content_key="mo" />
        const coupon_detail =
            apply_coupon && coupon && coupon?.invoice_name && `(${coupon.invoice_name})`
        const subscription_item =
            new_subscription_items && new_subscription_items[0]
        switch (purchase_type) {
            case PurchaseType.Upgrade:
                return (
                    <>
                        <span className="font-bold">
                            <Translation content_key="Upgrade to" /> &nbsp;
                            <Translation
                                content_key={subscription_item?.type || ''}
                            />
                        </span>
                        <span className="justify-self-end">
                            {formatAmount(
                                subscription_item?.subscriptionItemUnitPrice
                            )}{' '}
                            / {duration}
                        </span>

                        <Translation
                            render_as="span"
                            className="text-gray-500"
                            content_key="current_plan_paid"
                        />
                        <span className="justify-self-end text-gray-500">
                            -{formatAmount(current_plan?.amount)} /{' '}
                            {duration}
                        </span>

                        <div className="col-span-2 border-t my-3"></div>

                        <Translation
                            render_as="span"
                            content_key="subtotal_diff"
                        />
                        <span className="justify-self-end">
                            {formatAmount(subtotal)} / {duration}
                        </span>

                        <Translation
                            render_as="span"
                            content_key="coupon_detail"
                            content_values={{
                                coupon_detail:
                                    (coupon_detail as string) || '',
                            }}
                        />
                        <span className="justify-self-end">
                            -{formatAmount(discount)}
                        </span>

                        <Translation
                            render_as="span"
                            className="font-bold text-red-800"
                            content_key="total"
                        />

                        <span className="font-bold text-red-800 justify-self-end">
                            {formatAmount(total)} / {duration}
                        </span>
                    </>
                )

            case PurchaseType.Downgrade:
                return (
                    <>
                        <span className="font-bold">
                            <Translation content_key="downgrade_to" />{' '}
                            &nbsp;
                            <Translation
                                content_key={
                                    subscription_item?.type || 'Freemium'
                                }
                            />
                        </span>
                        <span className="justify-self-end">
                            {formatAmount(
                                subscription_item?.subscriptionItemUnitPrice
                            )}{' '}
                            / {duration}
                        </span>
                        <Translation
                            render_as="span"
                            className="text-gray-500"
                            content_key="current_plan"
                        />
                        <span className="justify-self-end text-gray-500 line-through">
                            {formatAmount(current_plan?.amount)} /{' '}
                            {duration}
                        </span>
                        <div className="col-span-2 border-t my-3"></div>
                        <Translation
                            render_as="span"
                            content_key="subtotal"
                        />
                        <span className="justify-self-end">
                            {formatAmount(subtotal)} / {duration}
                        </span>
                        <Translation
                            render_as="span"
                            content_key="coupon_detail"
                            content_values={{
                                coupon_detail: coupon_detail || '',
                            }}
                        />
                        <span className="justify-self-end">
                            -{formatAmount(discount)}
                        </span>
                        <Translation
                            render_as="span"
                            className="font-bold text-red-800"
                            content_key="total"
                        />
                        <span className="font-bold text-red-800 justify-self-end">
                            {formatAmount(total)} / {duration}
                        </span>
                    </>
                )

            default:
                return (
                    <>
                        {new_subscription_items?.map((item) => {
                            let subtotal =
                                item.subscriptionItemUnitPrice *
                                item.subscriptionItemQuantity
                            return (
                                <div
                                    key={item?.type}
                                    className="col-span-2 flex justify-between"
                                >
                                    <span className="font-bold">
                                        <Translation content_key="add" />{' '}
                                        &nbsp;
                                        <Translation
                                            content_key={item?.type || ''}
                                        />
                                        &nbsp; x &nbsp;
                                        {item?.subscriptionItemQuantity}
                                    </span>
                                    <span className="justify-self-end">
                                        {formatAmount(subtotal)} /{' '}
                                        {duration}
                                    </span>
                                </div>
                            )
                        })}

                        <div className="col-span-2 border-t my-3"></div>

                        <Translation
                            render_as="span"
                            content_key="subtotal"
                        />
                        <span className="justify-self-end">
                            {formatAmount(subtotal)} / {duration}
                        </span>

                        <Translation
                            render_as="span"
                            content_key="coupon_detail"
                            content_values={{
                                coupon_detail:
                                    (coupon_detail as string) || '',
                            }}
                        />
                        <span className="justify-self-end">
                            -{formatAmount(discount)}
                        </span>

                        <Translation
                            render_as="span"
                            className="font-bold text-red-800"
                            content_key="total"
                        />

                        <span className="font-bold text-red-800 justify-self-end">
                            {formatAmount(total)} / {duration}
                        </span>
                    </>
                )
        }
    }

    return (
        <ModalProvider.Visible>
            <ModalWrapper>
                <div className="bg-white shadow-2xl overflow-x-hidden sm:rounded-md relative border-2 border-gray-100 w-full p-5">
                    <div
                        className="flex flex-row justify-between text-gray-500 z-10 w-full"
                        onClick={() => setAttributes({})}
                    >
                        
                        <Translation
                            render_as="span"
                            className="circular inline-block text-xl text-primary-dark"
                            content_key="purchase_summary"
                        />
                        <PaymentModalCloser className="text-right text-xl" />
                    </div>
                    <div className="flex flex-col gap-y-5 w-full mt-3 text-black">
                        <div className="grid grid-cols-2 gap-y-2 justify-items-stretch border border-gray-300 text-black rounded-xl p-5">
                            <RenderPurchaseDetails />
                        </div>
                        <div className="flex flex-col gap-2">
                            <Switch.Group>
                                <div className="flex items-center">
                                    <Switch
                                        checked={apply_coupon}
                                        onChange={toggleApplyCoupon}
                                        className={classNames(
                                            'relative inline-flex items-center h-6 rounded-full w-11',
                                            apply_coupon
                                                ? 'bg-primary-light'
                                                : 'bg-gray-200'
                                        )}
                                    >
                                        <span
                                            className={`${apply_coupon
                                                ? 'translate-x-6'
                                                : 'translate-x-1'
                                                } inline-block w-4 h-4 transform bg-white rounded-full`}
                                        />
                                    </Switch>
                                    <Switch.Label className="ml-4" passive>
                                        <Translation content_key="apply_coupon_code" />
                                    </Switch.Label>
                                </div>
                            </Switch.Group>
                            {
                                apply_coupon && 
                                <input
                                type="text"
                                className={classNames(
                                    'px-6 py-3 mt-1 focus:ring-primary-light focus:border-primary-light block w-full border-gray-300 rounded-lg',
                                    apply_coupon ? '' : 'text-gray-400'
                                )}
                                disabled={!apply_coupon}
                                onChange={handleCouponChange}
                            />
                            }
                            
                            {coupon_message && (
                                <span
                                    className={coupon ? '' : 'text-red-500'}
                                >
                                    {coupon_message}
                                </span>
                            )}
                        </div>
                        <div className="flex flex-col gap-y-2">
                            <Translation
                                render_as="p"
                                content_key="payment"
                                className="flex"
                            />
                            <div className="flex">
                                <div className="bg-gray-300 bg-opacity-50 rounded-md px-4 py-2 mr-2">
                                    <i className="feather-credit-card text-primary text-xl mr-2 align-middle" />
                                    {primary_card?.last4 ? (
                                        <Translation
                                            render_as="span"
                                            className="align-middle"
                                            content_key="pay_with_ending"
                                            content_values={{
                                                brand: primary_card?.brand.toUpperCase(),
                                                last4: primary_card?.last4,
                                            }}
                                        />
                                    ) : (
                                        <Translation
                                            render_as="span"
                                            className="align-middle"
                                            content_key="no_payment_method"
                                            translations={translations}
                                        />
                                    )}
                                </div>
                                {!primary_card?.last4 && (
                                    <button
                                        type="button"
                                        className="bg-primary rounded-md text-white px-2 py-1 hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light"
                                        onClick={handleAddCard}
                                    >
                                        <Translation content_key="add_card" />
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="flex flex-col">
                            {api_error ? (
                                <div className="bg-red-100 rounded-md text-center p-2 mb-3 text-sm text-red-700">
                                    {api_error}
                                </div>
                            ) : (
                                ''
                            )}
                            <div className="flex justify-between mx-auto">
                                <button
                                    type="button"
                                    className="border border-gray-300 rounded-lg inline-block mr-3 px-2 py-1"
                                    onClick={() => {
                                        PaymentModal.close()
                                        setAttributes({})
                                    }}
                                >
                                    <Translation content_key="back_to_plan_selection" />
                                </button>
                                <button
                                    type="button"
                                    disabled={
                                        !primary_card?.last4 || purchasing
                                    }
                                    className={classNames(
                                        'group flex justify-center',
                                        'px-2 py-1 border border-transparent',
                                        'rounded-md text-white',
                                        !primary_card?.last4
                                            ? 'bg-gray-300'
                                            : 'bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light',
                                        api_error && 'bg-red-700',
                                        'disabled:bg-opacity-30 disabled:bg-primary-dark'
                                    )}
                                    onClick={handlePayment}
                                >
                                    <Translation content_key="confirm_and_pay_now" />
                                </button>
                            </div>
                        </div>
                    </div>
                    {display_thank_you_modal && (
                        <TierChangeModal
                            text={attributes?.selected_plan as string}
                            setDisplay={setDisplayThankYouModal}
                            display={display_thank_you_modal}
                        />
                    )}
                </div>
            </ModalWrapper>
        </ModalProvider.Visible>
    )
}

export interface IThankYouPurchase {
    text: string
    setDisplay: React.Dispatch<SetStateAction<boolean>>
    display: boolean
}
const TierChangeModal: React.FC<IThankYouPurchase> = ({
    text,
    setDisplay,
    display,
}) => {
    const { PaymentModal } = useAuth()
    const content = (
        <div>
            <div>
                <img
                    src={`https://${NEXT_PUBLIC_STATIC_DOMAIN}/images/completed-purchase.svg`}
                    className="w-full"
                    alt="purchase"
                />
            </div>
            <div className="mb-10 flex flex-col justify-center items-center">
                <div
                    style={{ color: '#23476B' }}
                    className="text-4xl font-bold mt-5 mb-5"
                >
                    <Translation content_key={text} />
                </div>
                {/* <div className="mb-10">
                    {[...Array(2)].map((_, index) => (
                        <div
                            className="flex justify-center mb-2"
                            key={index}
                        >
                            <p
                                style={{
                                    backgroundColor:
                                        'rgba(16, 185, 129, 0.1)',
                                }}
                                className="px-10 rounded-full"
                            >
                                PLAN OR ADD-ON INFO HERE - LINE {index + 1}
                            </p>
                        </div>
                    ))}
                </div> */}
                <div
                    className="px-10 text-center text-base mb-5"
                    style={{ color: '#23476B' }}
                >
                    <Translation content_key="thank_you_purchase" />
                </div>
                <button
                    className="px-10 rounded-full  py-3"
                    style={{
                        color: '#5E9FBD',
                        border: '1px solid #5E9FBD',
                    }}
                    onClick={() => {
                        setDisplay((prev) => !prev)
                        PaymentModal.close()
                    }}
                >
                    <Translation content_key="back_to_account" />
                </button>
            </div>
        </div>
    )
    return (
        <Transition
            appear
            show={display}
            as="div"
            enter="transition-opacity duration-75"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
        >
            <Dialog
                as="div"
                className="fixed inset-0 z-[102] overflow-y-auto"
                onClose={() => { }}
            >
                <div className="min-h-screen px-4 text-center">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Dialog.Overlay className="fixed inset-0 backdrop-filter backdrop-brightness-50" />
                    </Transition.Child>

                    <span
                        className="inline-block h-screen align-middle"
                        aria-hidden="true"
                    >
                        &#8203;
                    </span>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                    >
                        <div className="inline-block w-full sm:w-full md:w-8/12 lg:w-1/2 xl:w-1/2 2xl:w-4/12 overflow-hidden text-left align-middle sm:align-top md:align-top lg:align-middle xl:align-middle transition-all transform bg-white shadow-xl rounded-2xl ">
                            <span
                                className="text-sm inline-block w-5 h-5  text-center font-extralight cursor-pointer"
                                onClick={() => {
                                    setDisplay((prev) => !prev)
                                    PaymentModal.close()
                                }}
                                style={{
                                    color: 'rgb(35, 71, 107)',
                                    position: 'absolute',
                                    top: '3.5%',
                                    right: '3.1%',
                                }}
                            >
                                <XIcon />
                            </span>
                            {content}
                        </div>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition>
    )
}
